import React, { useState, useMemo } from "react";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, StatusBadge, LuxuryButton, EmptyState } from "@/components/lux";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import { ArrowDownToLine, Zap, Hand, FileSpreadsheet, FileText, Search, CheckCheck, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BscScanLink } from "@/components/DepositStatus";

const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]/50";
const PAGE = 10;

const normStatus = (s) => {
  const k = (s || "").toLowerCase();
  if (["approved", "paid"].includes(k)) return "Approved";
  if (["rejected", "cancelled", "failed"].includes(k)) return "Rejected";
  return "Pending";
};
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

export default function AdminWithdrawals() {
  const { data, loading, reload } = useFetch("/admin/withdrawals");
  const { data: cfg, reload: reloadCfg } = useFetch("/admin/config");
  const { data: lastBulk, reload: reloadBulk } = useFetch("/admin/last-bulk-action");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [applied, setApplied] = useState({ from: "", to: "" });
  const [page, setPage] = useState(1);
  const [viewRow, setViewRow] = useState(null);
  const [remark, setRemark] = useState("");
  const [statusSel, setStatusSel] = useState("Approved");
  const [selected, setSelected] = useState(() => new Set());
  const [statusFilter, setStatusFilter] = useState("all");
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [thr, setThr] = useState({ count: "", total: "" });
  React.useEffect(() => {
    if (cfg) setThr({ count: cfg.pending_alert_count ?? "", total: cfg.pending_alert_total ?? "" });
  }, [cfg]);
  const auto = !!cfg?.withdrawal_auto;

  const rows = data?.withdrawals || [];
  const filtered = useMemo(() => rows.filter((w) => {
    if (statusFilter !== "all" && normStatus(w.status) !== statusFilter) return false;
    if (!w.created_at) return true;
    const d = new Date(w.created_at);
    if (applied.from && d < new Date(applied.from + "T00:00:00")) return false;
    if (applied.to && d > new Date(applied.to + "T23:59:59")) return false;
    return true;
  }), [rows, applied, statusFilter]);

  const pendingIds = filtered.filter((w) => normStatus(w.status) === "Pending").map((w) => w.wd_id);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const curPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((curPage - 1) * PAGE, curPage * PAGE);
  const pendingAll = rows.filter((w) => normStatus(w.status) === "Pending");
  const pendingTotal = pendingAll.reduce((s, w) => s + Number(w.net || 0), 0);
  const lb = lastBulk?.last;

  const toggleMode = async () => {
    try { await api.post(`/admin/withdrawal-mode?auto=${!auto}`); toast.success(`Withdrawals set to ${!auto ? "AUTO" : "MANUAL"}`); reloadCfg(); reload(); }
    catch { toast.error("Failed"); }
  };
  const act = async (id, action, note) => {
    try {
      await api.post(`/admin/withdrawals/${id}/action?action=${action}${note ? `&note=${encodeURIComponent(note)}` : ""}`);
      toast.success(action === "paid" ? "Withdrawal approved & released" : "Withdrawal rejected — funds returned");
      reload(); setViewRow(null); setRemark(""); setStatusSel("Approved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const submitModal = () => act(viewRow.wd_id, statusSel === "Approved" ? "paid" : "reject", remark);

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(() => allSelected ? new Set() : new Set(pendingIds));
  const bulkAct = async (action) => {
    const ids = [...selected].filter((id) => pendingIds.includes(id));
    if (!ids.length) return toast.error("Select at least one pending request");
    try {
      const { data: r } = await api.post(`/admin/withdrawals/bulk-action?action=${action}`, { wd_ids: ids });
      toast.success(r.message); setSelected(new Set()); reload(); reloadBulk();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const bulkAllPending = async (action) => {
    if (!pendingIds.length) return toast.error("No pending requests in this filter");
    try {
      const { data: r } = await api.post(`/admin/withdrawals/bulk-action?action=${action}`, { wd_ids: pendingIds });
      toast.success(r.message); setSelected(new Set()); setBulkConfirm(null); reload(); reloadBulk();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveThresholds = async () => {
    try {
      await api.put("/admin/config", { pending_alert_count: Number(thr.count) || 0, pending_alert_total: Number(thr.total) || 0 });
      toast.success("Alert thresholds saved"); reloadCfg();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const exportRows = () => filtered.map((w, i) => ([
    i + 1, w.member_id || "", w.name || "", money(w.amount), money(w.fee), money(w.net),
    "Withdraw Request", normStatus(w.status), w.wallet_address || "", (w.note || "").replace(/[\r\n]+/g, " "), fmtDate(w.created_at),
  ]));
  const HEAD = ["#", "User ID", "Name", "Amount", "Deductions", "Payable Amount", "Type", "Status", "Wallet Address", "Remark", "Date"];
  const download = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  const exportCSV = () => {
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [HEAD, ...exportRows()].map((r) => r.map(esc).join(",")).join("\n");
    download("\uFEFF" + csv, "withdrawals.csv", "text/csv;charset=utf-8;");
  };
  const exportXLS = () => {
    const cell = (v) => `<td>${String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;")}</td>`;
    const body = [HEAD, ...exportRows()].map((r, i) => `<tr>${r.map((c) => (i === 0 ? `<th>${c}</th>` : cell(c))).join("")}</tr>`).join("");
    const html = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1">${body}</table></body></html>`;
    download(html, "withdrawals.xls", "application/vnd.ms-excel");
  };

  return (
    <div className="space-y-6">
      <PageTitle title="Withdrawal Requests" subtitle="Process member payout requests (BEP20 USDT)" right={
        <LuxuryButton variant={auto ? "gold" : "dark"} size="sm" onClick={toggleMode} data-testid="withdrawal-mode-toggle">
          {auto ? <><Zap size={14} className="mr-1 inline" /> Mode: AUTO</> : <><Hand size={14} className="mr-1 inline" /> Mode: MANUAL</>}
        </LuxuryButton>
      } />

      <div className="rounded-xl glass-2 p-3 text-xs text-[var(--text-dim)]" data-testid="wd-mode-note">
        {auto
          ? "AUTO mode: new withdrawal requests are released & marked Approved immediately (full payout, ledger + audit recorded)."
          : "MANUAL mode: select pending requests for bulk action, or open one to Approve (release payout) / Reject (return funds)."}
      </div>

      {pendingAll.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="pending-payouts-card">
          <div className="rounded-xl glass-2 p-4">
            <div className="text-xs text-[var(--text-dim)]">Pending Requests</div>
            <div className="mt-1 font-display text-2xl font-semibold text-white" data-testid="pending-count">{pendingAll.length}</div>
          </div>
          <div className="rounded-xl glass-2 p-4">
            <div className="text-xs text-[var(--text-dim)]">Total Pending Payout (net)</div>
            <div className="mt-1 gold-text font-display text-2xl font-semibold" data-testid="pending-total">${money(pendingTotal)}</div>
          </div>
        </div>
      )}

      <GlassCard className="p-4 sm:p-6" data-testid="threshold-settings">
        <h3 className="mb-1 font-semibold text-white">Pending Payout Alert Thresholds</h3>
        <p className="mb-3 text-xs text-[var(--text-dim)]">Notify the admin when pending requests count or total net payout crosses these values.</p>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="mb-1 block text-[11px] text-[var(--text-dim)]">Alert at count ≥</label>
            <input type="number" min="0" className={inp + " w-[150px]"} value={thr.count} onChange={(e) => setThr((s) => ({ ...s, count: e.target.value }))} data-testid="threshold-count" />
          </div>
          <div>
            <label className="mb-1 block text-[11px] text-[var(--text-dim)]">Alert at total ≥ ($)</label>
            <input type="number" min="0" step="any" className={inp + " w-[170px]"} value={thr.total} onChange={(e) => setThr((s) => ({ ...s, total: e.target.value }))} data-testid="threshold-total" />
          </div>
          <LuxuryButton size="sm" variant="gold" onClick={saveThresholds} data-testid="threshold-save">Save Thresholds</LuxuryButton>
        </div>
        <div className="mt-3 space-y-1 text-xs" data-testid="threshold-preview">
          <div className="text-[var(--text-dim)]">Currently <span className="text-white">{pendingAll.length}</span> pending — {Number(thr.count) ? (pendingAll.length >= Number(thr.count) ? `${pendingAll.length - Number(thr.count)} above` : `${Number(thr.count) - pendingAll.length} below`) + " the count alert" : "count alert off"}.</div>
          <div className="text-[var(--text-dim)]">Total <span className="text-white">${money(pendingTotal)}</span> — {Number(thr.total) ? (pendingTotal >= Number(thr.total) ? `$${money(pendingTotal - Number(thr.total))} above` : `$${money(Number(thr.total) - pendingTotal)} below`) + " the total alert" : "total alert off"}.</div>
          {lb && <div className="border-t border-white/10 pt-2 text-[var(--text-dim)]" data-testid="last-bulk-line">Last bulk action: <span className="text-white">{lb.action === "paid" ? "approved" : "rejected"} {lb.processed}</span> request(s) by {lb.admin || "admin"} · {fmtDate(lb.created_at)}</div>}
        </div>
      </GlassCard>

      <GlassCard className="p-4 sm:p-6 space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="mb-1 block text-[11px] text-[var(--text-dim)]">From</label>
              <input type="date" className={inp + " w-[170px]"} value={from} onChange={(e) => setFrom(e.target.value)} data-testid="wd-from-date" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-[var(--text-dim)]">To</label>
              <input type="date" className={inp + " w-[170px]"} value={to} onChange={(e) => setTo(e.target.value)} data-testid="wd-to-date" />
            </div>
            <LuxuryButton size="sm" onClick={() => { setApplied({ from, to }); setPage(1); }} data-testid="wd-search-btn"><Search size={14} className="mr-1 inline" /> Search</LuxuryButton>
            <div>
              <label className="mb-1 block text-[11px] text-[var(--text-dim)]">Status</label>
              <select className={inp + " w-[150px]"} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); setSelected(new Set()); }} data-testid="wd-status-filter">
                <option value="all">All</option>
                <option value="Pending">Pending only</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
            {(applied.from || applied.to) && <LuxuryButton size="sm" variant="dark" onClick={() => { setFrom(""); setTo(""); setApplied({ from: "", to: "" }); }}>Clear</LuxuryButton>}
          </div>
          <div className="flex gap-2">
            <LuxuryButton size="sm" variant="dark" onClick={exportXLS} data-testid="export-xls-btn"><FileSpreadsheet size={14} className="mr-1 inline" /> Export to XLS</LuxuryButton>
            <LuxuryButton size="sm" onClick={exportCSV} data-testid="export-csv-btn"><FileText size={14} className="mr-1 inline" /> Export to CSV</LuxuryButton>
          </div>
        </div>

        {pendingIds.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-xl glass-2 p-3" data-testid="wd-filtered-bulk-bar">
            <span className="text-sm text-[var(--text-dim)]">{pendingIds.length} pending in current filter</span>
            <LuxuryButton size="sm" variant="gold" onClick={() => setBulkConfirm("paid")} data-testid="wd-approve-all-pending"><CheckCheck size={14} className="mr-1 inline" /> Approve All Pending</LuxuryButton>
            <LuxuryButton size="sm" variant="dark" onClick={() => setBulkConfirm("reject")} data-testid="wd-reject-all-pending"><XCircle size={14} className="mr-1 inline" /> Reject All Pending</LuxuryButton>
          </div>
        )}

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[var(--gold)]/30 bg-[var(--gold)]/5 p-3" data-testid="wd-bulk-bar">
            <span className="text-sm text-white">{[...selected].filter((id) => pendingIds.includes(id)).length} selected</span>
            <LuxuryButton size="sm" variant="gold" onClick={() => bulkAct("paid")} data-testid="wd-bulk-approve"><CheckCheck size={14} className="mr-1 inline" /> Approve Selected</LuxuryButton>
            <LuxuryButton size="sm" variant="dark" onClick={() => bulkAct("reject")} data-testid="wd-bulk-reject"><XCircle size={14} className="mr-1 inline" /> Reject Selected</LuxuryButton>
            <button className="text-xs text-[var(--text-dim)] underline" onClick={() => setSelected(new Set())}>clear</button>
          </div>
        )}

        {loading ? <div className="skeleton h-40" /> : filtered.length ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-[var(--text-dim)]">
                  <tr className="border-b border-white/10">
                    <th className="py-3 pr-2">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={!pendingIds.length} data-testid="wd-select-all" />
                    </th>
                    <th className="py-3 pr-2">#</th><th className="py-3 pr-2">User ID</th><th className="py-3 pr-2">Name</th>
                    <th className="py-3 pr-2">Amount</th><th className="py-3 pr-2">Deductions</th><th className="py-3 pr-2">Payable</th>
                    <th className="py-3 pr-2">Type</th><th className="py-3 pr-2">Status</th><th className="py-3 pr-2">Tx</th><th className="py-3 pr-2">Wallet Address</th>
                    <th className="py-3 pr-2">Remark</th><th className="py-3 pr-2">Date</th><th className="py-3 pr-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((w, i) => {
                    const ds = normStatus(w.status);
                    const isPending = ds === "Pending";
                    return (
                      <tr key={w.wd_id} className="row-hover border-b border-white/5 align-middle" data-testid={`wd-row-${w.wd_id}`}>
                        <td className="py-3 pr-2">
                          {isPending && <input type="checkbox" checked={selected.has(w.wd_id)} onChange={() => toggleSel(w.wd_id)} data-testid={`wd-select-${w.wd_id}`} />}
                        </td>
                        <td className="py-3 pr-2 text-[var(--text-dim)]">{(curPage - 1) * PAGE + i + 1}</td>
                        <td className="py-3 pr-2 text-white">{w.member_id}</td>
                        <td className="py-3 pr-2">{w.name || "—"}</td>
                        <td className="py-3 pr-2">${money(w.amount)}</td>
                        <td className="py-3 pr-2 text-[var(--maroon)]">${money(w.fee)}</td>
                        <td className="py-3 pr-2 gold-text">${money(w.net)}</td>
                        <td className="py-3 pr-2 whitespace-nowrap text-[var(--text-dim)]">Withdraw Request</td>
                        <td className="py-3 pr-2"><StatusBadge status={ds.toLowerCase()} /></td>
                        <td className="py-3 pr-2" data-testid={`admin-wd-tx-${w.wd_id}`}>{w.tx_hash ? <BscScanLink hash={w.tx_hash} /> : <span className="text-[var(--text-dim)]">—</span>}</td>
                        <td className="py-3 pr-2 max-w-[140px] truncate text-[var(--text-dim)]" title={w.wallet_address}>{w.wallet_address}</td>
                        <td className="py-3 pr-2 max-w-[140px] truncate text-[var(--text-dim)]" title={w.note}>{w.note || "—"}</td>
                        <td className="py-3 pr-2 whitespace-nowrap text-[var(--text-dim)]">{fmtDate(w.created_at)}</td>
                        <td className="py-3 pr-2">
                          {isPending
                            ? <LuxuryButton size="sm" onClick={() => { setViewRow(w); setRemark(w.note || ""); setStatusSel("Approved"); }} data-testid={`wd-view-${w.wd_id}`}>View</LuxuryButton>
                            : <span className={ds === "Approved" ? "text-emerald-300" : "text-rose-300"}>{ds}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2 text-xs text-[var(--text-dim)]">
              <div>Showing {(curPage - 1) * PAGE + 1} to {Math.min(curPage * PAGE, filtered.length)} of {filtered.length} entries</div>
              <div className="flex items-center gap-1.5">
                <LuxuryButton size="sm" variant="dark" onClick={() => setPage(1)} disabled={curPage === 1}>First</LuxuryButton>
                <LuxuryButton size="sm" variant="dark" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={curPage === 1}>Previous</LuxuryButton>
                <span className="px-2 text-white">Page {curPage} / {totalPages}</span>
                <LuxuryButton size="sm" variant="dark" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={curPage === totalPages}>Next</LuxuryButton>
                <LuxuryButton size="sm" variant="dark" onClick={() => setPage(totalPages)} disabled={curPage === totalPages}>Last</LuxuryButton>
              </div>
            </div>
          </>
        ) : <EmptyState icon={ArrowDownToLine} title="No withdrawals" />}
      </GlassCard>

      <Dialog open={!!viewRow} onOpenChange={(o) => !o && setViewRow(null)}>
        <DialogContent className="border-white/10 bg-[var(--bg-2)] text-white" data-testid="wd-modal">
          <DialogHeader><DialogTitle>Withdraw Request</DialogTitle></DialogHeader>
          {viewRow && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="rounded-lg glass-2 p-2"><div className="text-[var(--text-dim)]">Amount</div><div className="font-semibold">${money(viewRow.amount)}</div></div>
                <div className="rounded-lg glass-2 p-2"><div className="text-[var(--text-dim)]">Payable</div><div className="gold-text font-semibold">${money(viewRow.net)}</div></div>
              </div>
              <div>
                <label className="mb-1 block text-xs text-[var(--text-dim)]">User ID</label>
                <input className={inp} value={viewRow.member_id || ""} readOnly data-testid="wd-modal-userid" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-[var(--text-dim)]">Wallet Address</label>
                <input className={inp + " text-[var(--text-dim)]"} value={viewRow.wallet_address || ""} readOnly />
              </div>
              <div>
                <label className="mb-1 block text-xs text-[var(--text-dim)]">Remark</label>
                <textarea className={inp + " min-h-[80px]"} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Optional note for the member" data-testid="wd-modal-remark" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-[var(--text-dim)]">Status</label>
                <select className={inp} value={statusSel} onChange={(e) => setStatusSel(e.target.value)} data-testid="wd-modal-status">
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
              <LuxuryButton onClick={submitModal} variant={statusSel === "Approved" ? "gold" : "dark"} data-testid="wd-modal-submit">
                {statusSel === "Approved" ? "Approve & Release Payout" : "Reject & Return Funds"}
              </LuxuryButton>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!bulkConfirm} onOpenChange={(o) => !o && setBulkConfirm(null)}>
        <DialogContent className="border-white/10 bg-[var(--bg-2)] text-white" data-testid="bulk-confirm-modal">
          <DialogHeader><DialogTitle>{bulkConfirm === "paid" ? "Approve All Pending" : "Reject All Pending"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[var(--text-dim)]">
              You are about to <span className={bulkConfirm === "paid" ? "text-emerald-300" : "text-rose-300"}>{bulkConfirm === "paid" ? "release payout for" : "reject"}</span> all pending requests in the current filter.
            </p>
            <div className="space-y-2 rounded-xl glass-2 p-3 text-sm">
              <div className="flex justify-between"><span className="text-[var(--text-dim)]">Requests</span><span className="font-semibold" data-testid="bulk-confirm-count">{pendingIds.length}</span></div>
              <div className="flex justify-between"><span className="text-[var(--text-dim)]">Total {bulkConfirm === "paid" ? "to release" : "to return"} (net)</span><span className="gold-text font-bold" data-testid="bulk-confirm-total">${money(filtered.filter((w) => normStatus(w.status) === "Pending").reduce((s, w) => s + Number(w.net || 0), 0))}</span></div>
            </div>
            <div className="flex gap-2">
              <LuxuryButton variant="dark" className="flex-1" onClick={() => setBulkConfirm(null)} data-testid="bulk-confirm-cancel">Cancel</LuxuryButton>
              <LuxuryButton variant={bulkConfirm === "paid" ? "gold" : "dark"} className="flex-1" onClick={() => bulkAllPending(bulkConfirm)} data-testid="bulk-confirm-submit">
                {bulkConfirm === "paid" ? "Confirm & Release" : "Confirm & Reject"}
              </LuxuryButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
